<template>
  <div>

    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-2xl font-bold text-slate-800">Sistemas por Cliente</h2>
        <p class="text-slate-500 text-sm mt-1">Asigna sistemas, vigencias y planes a cada cliente.</p>
      </div>
      <!-- Tab bitácora -->
      <div class="flex gap-2">
        <button
          @click="tab = 'sistemas'"
          :class="['px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200',
                   tab === 'sistemas'
                     ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200'
                     : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-400']"
        >
          <i class="fa-solid fa-grid-2 mr-1.5"></i>Sistemas
        </button>
        <button
          @click="abrirBitacora"
          :class="['px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200',
                   tab === 'bitacora'
                     ? 'bg-slate-700 text-white shadow-lg'
                     : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400']"
        >
          <i class="fa-solid fa-clock-rotate-left mr-1.5"></i>Bitácora
        </button>
      </div>
    </div>

    <!-- ─── TAB SISTEMAS ─────────────────────────────────────────── -->
    <div v-if="tab === 'sistemas'">

      <!-- Selector de cliente -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
        <label class="block text-sm font-semibold text-slate-700 mb-2">
          <i class="fa-solid fa-user mr-1.5 text-emerald-500"></i>Selecciona un cliente
        </label>
        <select
          v-model="clienteSeleccionado"
          @change="cargarSistemas"
          class="w-full sm:w-96 rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
        >
          <option value="">— Elige un cliente —</option>
          <option v-for="c in clientes" :key="c.idUsuario" :value="c.idUsuario">
            {{ c.nombre }} ({{ c.email }}){{ c.esAdmin ? ' — Admin' : '' }}
          </option>
        </select>
      </div>

      <!-- Tabla de sistemas -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200">
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Sistema</th>
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Inicio</th>
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Vencimiento</th>
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                <th class="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">

              <tr v-if="!clienteSeleccionado">
                <td colspan="6" class="px-5 py-12 text-center text-slate-400">
                  <i class="fa-solid fa-arrow-up text-2xl mb-2 block text-slate-300"></i>
                  Selecciona un cliente para ver sus sistemas
                </td>
              </tr>

              <tr v-else-if="cargandoSistemas">
                <td colspan="6" class="px-5 py-12 text-center text-slate-400">
                  <i class="fa-solid fa-spinner fa-spin text-2xl text-emerald-400 mr-2"></i>Cargando...
                </td>
              </tr>

              <tr v-else-if="sistemas.length === 0">
                <td colspan="6" class="px-5 py-12 text-center text-slate-400">
                  <i class="fa-solid fa-inbox text-3xl mb-2 block"></i>Sin sistemas disponibles
                </td>
              </tr>

              <tr
                v-else
                v-for="s in sistemas"
                :key="s.idSistema"
                class="hover:bg-slate-50 transition-colors duration-150"
                :class="{ 'opacity-50': s.estatus === 'I' }"
              >
                <!-- Sistema -->
                <td class="px-5 py-4">
                  <div class="flex items-center gap-3">
                    <div
                      class="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 shadow-sm"
                      :style="{ background: s.gradiente }"
                    >
                      {{ s.icono }}
                    </div>
                    <div>
                      <p class="font-semibold text-slate-800">{{ s.nombreSistema }}</p>
                      <p class="text-xs text-slate-400 max-w-xs truncate">{{ s.descripcion }}</p>
                    </div>
                  </div>
                </td>

                <!-- Plan -->
                <td class="px-5 py-4">
                  <span v-if="s.idUsuarioSistema && s.nombrePlan" class="text-slate-700 font-medium">
                    {{ s.nombrePlan }}
                    <span class="text-slate-400 text-xs block">${{ formatPrecio(s.precio) }}/mes</span>
                  </span>
                  <span v-else class="text-slate-400 text-xs italic">Sin plan</span>
                </td>

                <!-- Fechas -->
                <td class="px-5 py-4 text-slate-600 text-xs">
                  {{ s.fechaInicio ? formatFecha(s.fechaInicio) : '—' }}
                </td>
                <td class="px-5 py-4 text-slate-600 text-xs">
                  {{ s.fechaVencimiento ? formatFecha(s.fechaVencimiento) : '—' }}
                </td>

                <!-- Estado -->
                <td class="px-5 py-4">
                  <span :class="['inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold', badgeEstado(s)]">
                    {{ labelEstado(s) }}
                  </span>
                </td>

                <!-- Acciones -->
                <td class="px-5 py-4">
                  <div class="flex items-center gap-2">
                    <!-- Asignar / Editar vigencia -->
                    <button
                      @click="abrirModal(s)"
                      :title="s.idUsuarioSistema ? 'Editar vigencia' : 'Asignar sistema'"
                      class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      :class="s.idUsuarioSistema
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'"
                    >
                      <i :class="s.idUsuarioSistema ? 'fa-solid fa-pen' : 'fa-solid fa-plus'"></i>
                    </button>
                    <!-- Activar / Inactivar (solo si ya está asignado) -->
                    <button
                      v-if="s.idUsuarioSistema"
                      @click="toggleEstatus(s)"
                      :title="s.estatus === 'A' ? 'Inactivar' : 'Activar'"
                      class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      :class="s.estatus === 'A'
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'"
                    >
                      <i :class="s.estatus === 'A' ? 'fa-solid fa-ban' : 'fa-solid fa-check'"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ─── TAB BITÁCORA ─────────────────────────────────────────── -->
    <div v-if="tab === 'bitacora'">
      <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

        <!-- Filtro por cliente -->
        <div class="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <label class="text-sm font-semibold text-slate-600 whitespace-nowrap">Filtrar por cliente:</label>
          <select
            v-model="bitacoraFiltroCliente"
            @change="cargarBitacora"
            class="w-full sm:w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
          >
            <option value="">Todos los clientes</option>
            <option v-for="c in clientes" :key="c.idUsuario" :value="c.idUsuario">{{ c.nombre }}</option>
          </select>
          <button
            @click="cargarBitacora"
            class="px-4 py-2 bg-slate-700 text-white text-sm rounded-xl hover:bg-slate-600 transition-colors"
          >
            <i class="fa-solid fa-rotate-right mr-1"></i>Actualizar
          </button>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200">
                <th class="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                <th class="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Acción</th>
                <th class="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Descripción</th>
                <th class="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Realizado por</th>
                <th class="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-if="cargandoBitacora">
                <td colspan="5" class="px-5 py-10 text-center text-slate-400">
                  <i class="fa-solid fa-spinner fa-spin text-xl text-slate-400 mr-2"></i>Cargando bitácora...
                </td>
              </tr>
              <tr v-else-if="bitacoraRegistros.length === 0">
                <td colspan="5" class="px-5 py-10 text-center text-slate-400">
                  <i class="fa-solid fa-clock-rotate-left text-3xl mb-2 block text-slate-300"></i>
                  Sin registros en la bitácora
                </td>
              </tr>
              <tr
                v-else
                v-for="b in bitacoraRegistros"
                :key="b.idBitacora"
                class="hover:bg-slate-50 transition-colors"
              >
                <td class="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                  {{ formatDateTime(b.fechaHora) }}
                </td>
                <td class="px-5 py-3">
                  <span :class="['inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold', badgeAccion(b.accion)]">
                    {{ b.accion }}
                  </span>
                </td>
                <td class="px-5 py-3 text-slate-700 max-w-xs">{{ b.descripcion }}</td>
                <td class="px-5 py-3 text-slate-600 text-xs">{{ b.nombreAdmin }}</td>
                <td class="px-5 py-3 text-slate-500 text-xs">{{ b.nombreCliente || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ─── MODAL ASIGNAR / EDITAR VIGENCIA ──────────────────────── -->
    <transition name="modal">
      <div
        v-if="modalAbierto"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        style="background:rgba(0,0,0,0.5);"
        @click.self="cerrarModal"
      >
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md">

          <!-- Header -->
          <div
            class="p-6 rounded-t-2xl text-white relative overflow-hidden"
            :style="{ background: form.gradiente || 'linear-gradient(135deg,#10B981,#065F46)' }"
          >
            <div class="absolute inset-0 bg-black/20"></div>
            <div class="relative flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="text-3xl">{{ form.icono }}</span>
                <div>
                  <p class="text-white/70 text-xs uppercase tracking-wider">
                    {{ form.idUsuarioSistema ? 'Editar vigencia' : 'Asignar sistema' }}
                  </p>
                  <h3 class="font-bold text-lg leading-tight">{{ form.nombreSistema }}</h3>
                </div>
              </div>
              <button @click="cerrarModal" class="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <i class="fa-solid fa-xmark text-sm"></i>
              </button>
            </div>
          </div>

          <!-- Body -->
          <div class="p-6 space-y-4">

            <!-- Plan -->
            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1.5">
                Plan <span class="text-slate-400 font-normal">(opcional)</span>
              </label>
              <select
                v-model="form.idPlan"
                class="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option :value="null">— Sin plan asignado —</option>
                <option v-for="p in planesDelSistema" :key="p.idPlan" :value="p.idPlan">
                  {{ p.nombrePlan }} — ${{ formatPrecio(p.precio) }}/mes
                </option>
              </select>
            </div>

            <!-- Estatus -->
            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1.5">Estatus</label>
              <div class="flex gap-3">
                <label class="flex items-center gap-2 cursor-pointer select-none">
                  <input type="radio" v-model="form.estatus" value="A" class="accent-emerald-600" />
                  <span class="text-sm text-slate-700">Activo</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer select-none">
                  <input type="radio" v-model="form.estatus" value="I" class="accent-red-500" />
                  <span class="text-sm text-slate-700">Inactivo</span>
                </label>
              </div>
            </div>

            <!-- Fechas -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">Fecha inicio</label>
                <input
                  type="date"
                  v-model="form.fechaInicio"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">Fecha vencimiento</label>
                <input
                  type="date"
                  v-model="form.fechaVencimiento"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>

            <!-- Preview vigencia -->
            <div v-if="form.fechaVencimiento" class="bg-slate-50 rounded-xl px-4 py-3 text-xs text-slate-500">
              <i class="fa-solid fa-calendar-check mr-1 text-emerald-500"></i>
              Vence el <strong class="text-slate-700">{{ formatFecha(form.fechaVencimiento) }}</strong>
              <span v-if="diasRestantesForm !== null">
                — <strong :class="diasRestantesForm <= 0 ? 'text-red-500' : diasRestantesForm <= 7 ? 'text-amber-500' : 'text-emerald-600'">
                  {{ diasRestantesForm <= 0 ? 'Vencido' : diasRestantesForm + ' días restantes' }}
                </strong>
              </span>
            </div>

          </div><!-- /body scroll -->

          <!-- Footer -->
          <div class="px-6 pb-6 flex gap-3 justify-end">
            <button @click="cerrarModal" class="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
              Cancelar
            </button>
            <button
              @click="guardar"
              :disabled="guardando"
              class="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-lg shadow-emerald-200 transition-all duration-200"
            >
              <i v-if="guardando" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-floppy-disk"></i>
              {{ form.idUsuarioSistema ? 'Actualizar' : 'Asignar' }}
            </button>
          </div>

        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import servicioUS     from '@/services/usuariossistemas.service'
import servicioCli    from '@/services/clientes.service'
import servicioPlan   from '@/services/planes.service'

export default {
  name: 'SistemasClientes',

  data () {
    return {
      tab:                  'sistemas',

      // Datos maestros
      clientes:             [],
      clienteSeleccionado:  '',

      // Sistemas del cliente seleccionado
      sistemas:             [],
      cargandoSistemas:     false,

      // Bitácora
      bitacoraRegistros:    [],
      cargandoBitacora:     false,
      bitacoraFiltroCliente: '',

      // Modal
      modalAbierto:         false,
      guardando:            false,
      form: {
        idUsuarioSistema: 0,
        idSistema:        0,
        idPlan:           null,
        fechaInicio:      '',
        fechaVencimiento: '',
        estatus:          'A',
        // Solo lectura visual
        nombreSistema:    '',
        icono:            '',
        gradiente:        ''
      },
      planesDelSistema:     []
    }
  },

  computed: {
    diasRestantesForm () {
      if (!this.form.fechaVencimiento) return null
      const hoy  = new Date(); hoy.setHours(0, 0, 0, 0)
      const venc = new Date(this.form.fechaVencimiento + 'T00:00:00')
      return Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24))
    },
    idAdmin () {
      return parseInt(localStorage.getItem('idUsuario') || '0')
    }
  },

  created () {
    this.cargarClientes()
  },

  methods: {
    // ── CLIENTES ──────────────────────────────────────────────────────
    async cargarClientes () {
      try {
        const res = await servicioCli.clientesCon()
        this.clientes = res.data.clientes || []
      } catch { this.clientes = [] }
    },

    // ── SISTEMAS ──────────────────────────────────────────────────────
    async cargarSistemas () {
      if (!this.clienteSeleccionado) { this.sistemas = []; return }
      this.cargandoSistemas = true
      try {
        const res = await servicioUS.conAdmin(this.clienteSeleccionado)
        this.sistemas = res.data.sistemas || []
      } catch {
        this.sistemas = []
      } finally {
        this.cargandoSistemas = false
      }
    },

    // ── MODAL ─────────────────────────────────────────────────────────
    async abrirModal (s) {
      this.planesDelSistema = []
      this.form = {
        idUsuarioSistema: s.idUsuarioSistema || 0,
        idSistema:        s.idSistema,
        idPlan:           s.idPlan || null,
        fechaInicio:      s.fechaInicio ? s.fechaInicio.substring(0, 10) : '',
        fechaVencimiento: s.fechaVencimiento ? s.fechaVencimiento.substring(0, 10) : '',
        estatus:          s.estatus || 'A',
        nombreSistema:    s.nombreSistema,
        icono:            s.icono,
        gradiente:        s.gradiente
      }

      // Cargar planes del sistema para el combo
      try {
        const res = await servicioPlan.planesCon(s.idSistema)
        this.planesDelSistema = res.data.planes || []
      } catch {
        this.planesDelSistema = []
      }

      this.modalAbierto = true
    },

    cerrarModal () {
      this.modalAbierto = false
    },

    async guardar () {
      this.guardando = true
      try {
        const payload = {
          idUsuarioSistema: this.form.idUsuarioSistema,
          idUsuario:        parseInt(this.clienteSeleccionado),
          idSistema:        this.form.idSistema,
          idPlan:           this.form.idPlan || null,
          fechaInicio:      this.form.fechaInicio     || null,
          fechaVencimiento: this.form.fechaVencimiento || null,
          estatus:          this.form.estatus,
          idUsuarioAccion:  this.idAdmin
        }
        const res = await servicioUS.guardar(payload)
        if (res.data.resultado) {
          this.$store.dispatch('mostrarToast', { mensaje: res.data.mensaje, tipo: 'success' })
          this.modalAbierto = false
          await this.cargarSistemas()
        } else {
          this.$store.dispatch('mostrarToast', { mensaje: res.data.mensaje, tipo: 'warning' })
        }
      } catch (e) {
        this.$store.dispatch('mostrarToast', { mensaje: 'Error al guardar.', tipo: 'error' })
      } finally {
        this.guardando = false
      }
    },

    // ── TOGGLE ESTATUS ────────────────────────────────────────────────
    async toggleEstatus (s) {
      const nuevoEstatus = s.estatus === 'A' ? 'I' : 'A'
      const etiqueta     = nuevoEstatus === 'I' ? 'inactivar' : 'activar'

      const ok = await this.$store.dispatch('mostrarConfirm', {
        titulo:  `¿${etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1)} sistema?`,
        mensaje: `¿Deseas ${etiqueta} el sistema "${s.nombreSistema}" para este cliente?`
      })
      if (!ok) return

      try {
        const res = await servicioUS.cambiarEstatus({
          idUsuarioSistema: s.idUsuarioSistema,
          estatus:          nuevoEstatus,
          idUsuarioAccion:  this.idAdmin
        })
        this.$store.dispatch('mostrarToast', { mensaje: res.data.mensaje, tipo: res.data.resultado ? 'success' : 'warning' })
        if (res.data.resultado) await this.cargarSistemas()
      } catch {
        this.$store.dispatch('mostrarToast', { mensaje: 'Error al cambiar estatus.', tipo: 'error' })
      }
    },

    // ── BITÁCORA ──────────────────────────────────────────────────────
    async abrirBitacora () {
      this.tab = 'bitacora'
      await this.cargarBitacora()
    },

    async cargarBitacora () {
      this.cargandoBitacora = true
      try {
        const id  = this.bitacoraFiltroCliente || undefined
        const res = await servicioUS.bitacora(id)
        this.bitacoraRegistros = res.data.registros || []
      } catch {
        this.bitacoraRegistros = []
      } finally {
        this.cargandoBitacora = false
      }
    },

    // ── HELPERS ───────────────────────────────────────────────────────
    formatFecha (fecha) {
      if (!fecha) return '—'
      return new Date(fecha + (fecha.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    },

    formatDateTime (dt) {
      return new Date(dt).toLocaleString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    formatPrecio (v) {
      return Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },

    badgeEstado (s) {
      const e = s.estadoVigencia
      if (e === 'no_asignado') return 'bg-slate-100 text-slate-500'
      if (e === 'activo')      return 'bg-emerald-100 text-emerald-700'
      if (e === 'proximo')     return 'bg-amber-100 text-amber-700'
      if (e === 'vencido')     return 'bg-red-100 text-red-600'
      if (e === 'inactivo')    return 'bg-slate-100 text-slate-500'
      return 'bg-blue-100 text-blue-600'
    },

    labelEstado (s) {
      const e = s.estadoVigencia
      if (e === 'no_asignado') return '⬜ No asignado'
      if (e === 'activo')      return '✅ Activo'
      if (e === 'proximo')     return `⚠️ Vence en ${s.diasRestantes}d`
      if (e === 'vencido')     return '🔒 Vencido'
      if (e === 'inactivo')    return '🚫 Inactivo'
      return '📅 Sin fecha'
    },

    badgeAccion (accion) {
      if (accion === 'ASIGNAR')    return 'bg-blue-100 text-blue-700'
      if (accion === 'ACTUALIZAR') return 'bg-emerald-100 text-emerald-700'
      if (accion === 'ESTATUS')    return 'bg-amber-100 text-amber-700'
      return 'bg-slate-100 text-slate-600'
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: all .25s ease; }
.modal-enter, .modal-leave-to           { opacity: 0; transform: scale(.96); }
</style>
